import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OrderService } from 'src/app/services/order.service';
import { OrderGroup } from 'src/app/common/OrderGroup';
import { reservationFailureText } from 'src/app/common/ReservationFailure';
import { isReservationPending, ReservationStatus } from 'src/app/common/ReservationStatus';
import { ActivatedRoute } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { interval, startWith, switchMap, take, takeWhile } from 'rxjs';
import { OrderGroupOrderItemComponent } from '../order-group-order-item/order-group-order-item.component';

/** ~2 minutes at 1.5 s. Then the last read stays on screen; a reload picks the polling back up. */
const POLL_ATTEMPTS = 80;

/**
 * The detail page of one purchase (`/shop-order-group-edit/:id` — the URL keeps its old name, the
 * checkout lands here). It edits nothing: it follows a reservation that is still open, and once
 * the purchase is reserved it lets the buyer request the cancellation of positions.
 */
@Component({
    imports: [CommonModule, OrderGroupOrderItemComponent],
    selector: 'app-order-group-detail',
    templateUrl: './order-group-detail.component.html'
})
export class OrderGroupDetailComponent implements OnInit {
  private orderService = inject(OrderService);
  private activatedRoute = inject(ActivatedRoute);
  private location = inject(Location);

  private destroyRef = inject(DestroyRef);

  public orderGroup: OrderGroup;
  private orderGroupId: string;

  // Expose the enum to the template.
  public readonly ReservationStatus = ReservationStatus;

  /** #168: Unknown renders like PendingReservation — never as a silently completed purchase. */
  public get isPending(): boolean {
    return isReservationPending(this.orderGroup?.reservationStatus);
  }

  /**
   * Only an explicit Reserved is a completed purchase (#456); a pending, failed or unknown one
   * has nothing to cancel yet — a failed one was never bought at all.
   */
  public get isReserved(): boolean {
    return this.orderGroup?.reservationStatus === ReservationStatus.Reserved;
  }

  /** Backend #706: why the reservation failed, or null — then the alert says only that it did. */
  public get failureText(): string | null {
    return reservationFailureText(this.orderGroup);
  }

  ngOnInit(): void {
    this.orderGroupId = this.activatedRoute.snapshot.paramMap.get('id');

    // #456: the checkout may return before the reservation is confirmed on the bus. Poll while the
    // group is still PendingReservation so the buyer sees the eventual outcome (Reserved / Failed);
    // takeWhile(..., true) emits the first terminal value and then stops.
    //
    // #168: the predicate compared against a numeric enum while the API sends a string, so it was
    // false on the first emission and this "poll" fetched exactly once. It goes through
    // isReservationPending now, which also keeps polling on a status this build does not know —
    // treating an unrecognised value as terminal would leave the buyer looking at a stale order.
    //
    // POLL_ATTEMPTS caps the loop at ~2 minutes. Before #168 the bug ended it after one request;
    // now that it really polls, an order that never resolves would otherwise hammer the API for
    // as long as the tab stays open.
    interval(1500)
      .pipe(
        startWith(0),
        take(POLL_ATTEMPTS),
        switchMap(() => this.orderService.getPurchaseOrderGroupsById(this.orderGroupId)),
        takeWhile((og: OrderGroup) => isReservationPending(og?.reservationStatus), true),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (orderGroup: OrderGroup) => {
          this.orderGroup = orderGroup;
          // Failed is terminal, so this runs once per page. The backend's prose goes to the
          // console only (#706): English, with a SKU id.
          if (orderGroup?.reservationStatus === ReservationStatus.Failed) {
            console.warn(
              'Reservation failed',
              orderGroup.reservationFailureCode,
              orderGroup.reservationFailureReason,
            );
          }
        },
      });
  }

  /** After a cancellation request: the positions' new status comes from the backend, not from here. */
  reload(): void {
    this.orderService
      .getPurchaseOrderGroupsById(this.orderGroupId)
      .subscribe((orderGroup) => (this.orderGroup = orderGroup));
  }

  onBackClicked() {
    this.location.back();
  }
}
